import React, { useEffect, useMemo, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import { Controller } from 'react-hook-form';
import { Col, Input, Row } from 'reactstrap';
import Datepicker from 'components/Inputs/DatePicker';
import { useUsers } from 'views/Report/Protocol/hooks/useUsers';
import { useListOpportunityStagesByRecordType } from 'views/CRM/OpportunityStages/hooks/useListOpportunityStagesByRecordType';
import FlexSelect from 'components/Inputs/FlexSelect';
import { opportunityTypes } from 'views/CRM/shared/utils/opportunityTypes';
import { useGetLostReasonByEntity } from 'views/CRM/hooks/useGetLostReasonByEntity';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';

const dateRules = {
  required: 'Data é obrigatória',
  pattern: {
    value: /^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9][0-9][0-9]$/,
    message: 'Data inválida! (dd/MM/aaaa)'
  }
};
const temperatureOptions = [
  { label: 'Quente', value: 10 },
  { label: 'Morno', value: 5 },
  { label: 'Frio', value: 1 }
];
const getDateBegin = () => {
  const date = new Date();
  const firstDayMonth = new Date(
    date.getFullYear('pt-br'),
    date.getMonth('pt-br'),
    1
  );
  return format(startOfMonth(firstDayMonth), 'dd/LL/yyyy');
};
const toISOFormat = (dateTimeString) => {
  const [date] = dateTimeString.split(' ');
  const [DD, MM, YYYY] = date.split('/');
  return `${DD}/${MM}/${YYYY}`;
};

const DefaultFieldsFilterForm = ({
  control,
  setValue,
  getValues,
  recordTypeId,
  register
}) => {
  const { userList } = useUsers();
  const [filterListLostReason, setFilterListLostReason] = useState([]);

  const {
    opportunityStages,
    getOpportunityStagesByRecordType,
    isLoadingOpportunityStages
  } = useListOpportunityStagesByRecordType();

  const { listLostReason } = useGetLostReasonByEntity({
    entity: entitiesName.OPPORTUNITY,
    recordTypeId
  });

  useEffect(() => {
    if (!listLostReason) return;

    const options = listLostReason[0]?.options || [];
    const formattedOptions = options
      .filter((option) => option.recordTypeIds.includes(Number(recordTypeId)))
      .map((option) => ({ label: option.value, value: option.value }));

    setFilterListLostReason(formattedOptions);
  }, [listLostReason]);

  const showAllOpportunities = useMemo(() => {
    const flag = localStorage.getItem('show_all_opportunities');
    return flag === 'true';
  }, []);

  useEffect(() => {
    getOpportunityStagesByRecordType(recordTypeId);
  }, [getOpportunityStagesByRecordType, recordTypeId]);

  return (
    <>
      <Row className="mb-3">
        <Col sm="8">
          <label className="form-control-label">Buscar</label>
          <Controller
            as={
              <Input
                type="text"
                name="textQuery"
                placeholder="Buscar por título da oportunidade ou nome da conta"
              />
            }
            control={control}
            name="textQuery"
            defaultValue=""
          />
        </Col>
        <Col sm="4">
          <label className="form-control-label">Fase da Oportunidade</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={opportunityStages || []}
                isMulti={true}
                value={props.value}
                valueController={setValue}
                fieldName="stageId"
                labelName="name"
                valueName="id"
                isClearable={true}
                disabled={isLoadingOpportunityStages}
                closeMenuOnSelect={false}
              />
            )}
            control={control}
            name="stageId"
            defaultValue=""
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col sm={showAllOpportunities ? '4' : '6'}>
          <label className="form-control-label">Data inicial</label>
          <Controller
            render={(props) => (
              <Datepicker
                fieldName="dateBegin"
                valueController={setValue}
                defaultValue={props.value}
              />
            )}
            control={control}
            name="dateBegin"
            rules={dateRules}
            defaultValue={getDateBegin()}
          />
        </Col>
        <Col sm={showAllOpportunities ? '4' : '6'}>
          <label className="form-control-label">Data final</label>
          <Controller
            render={(props) => (
              <Datepicker
                fieldName="dateEnd"
                valueController={setValue}
                defaultValue={props.value}
              />
            )}
            control={control}
            name="dateEnd"
            defaultValue={new Date().toLocaleDateString('pt-br')}
            rules={{
              ...dateRules,
              validate: async (value) => {
                if (
                  new Date(toISOFormat(value)) <
                  new Date(toISOFormat(getValues()['dateBegin']))
                ) {
                  return 'A data final não pode ser anterior à inicial!';
                } else {
                  return true;
                }
              }
            }}
          />
        </Col>
        {showAllOpportunities && (
          <Col sm="4">
            <label className="form-control-label">Responsável</label>
            <Controller
              render={(props) => (
                <FlexSelect
                  dataOptions={userList || []}
                  isMulti={true}
                  value={props.value}
                  valueController={setValue}
                  fieldName="responsibleLogin"
                  labelName="name"
                  valueName="login"
                  isClearable={true}
                  closeMenuOnSelect={false}
                />
              )}
              control={control}
              name="responsibleLogin"
              defaultValue=""
            />
          </Col>
        )}
      </Row>
      <Row className="mb-3">
        <Col sm="4">
          <label className="form-control-label">Tipo de Oportunidade</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={opportunityTypes || []}
                isMulti={false}
                value={props.value}
                valueController={setValue}
                fieldName="type"
                labelName="name"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
              />
            )}
            control={control}
            defaultValue={null}
            name="type"
          />
        </Col>
        <Col sm="4">
          <label className="form-control-label">Motivo de perda</label>
          <Controller
            render={(props) => (
              <FlexSelect
                isClearable={true}
                placeholder="Selecione o motivo"
                dataOptions={filterListLostReason || []}
                isMulti={false}
                closeMenuOnSelect={true}
                value={props.value}
                valueController={setValue}
                fieldName="lostReason"
                labelName="value"
                valueName="value"
              />
            )}
            control={control}
            name="lostReason"
            defaultValue={null}
          />
        </Col>
        <Col sm="4">
          <label className="form-control-label">Temperatura</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={temperatureOptions || []}
                isMulti={true}
                value={props.value}
                valueController={setValue}
                fieldName="temperature"
                labelName="label"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={false}
              />
            )}
            control={control}
            name="temperature"
            defaultValue=""
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col sm="12">
          <label
            className="form-control-label"
            style={{ marginRight: '20px', fontWeight: '400' }}
          >
            <input
              type="radio"
              name="filterDateBy"
              ref={register}
              value="creation"
              defaultChecked={true}
            />{' '}
            Data de criação
          </label>
          <label className="form-control-label" style={{ fontWeight: '400' }}>
            <input
              type="radio"
              name="filterDateBy"
              ref={register}
              value="last_update"
              defaultChecked={false}
            />{' '}
            Data de última alteração
          </label>
          <label
            className="form-control-label"
            style={{ fontWeight: '400', marginLeft: '20px' }}
          >
            <input
              type="radio"
              name="filterDateBy"
              ref={register}
              value="closed"
              defaultChecked={false}
            />{' '}
            Data de fechamento
          </label>
        </Col>
      </Row>
    </>
  );
};

export default DefaultFieldsFilterForm;
